import React, { useState, useEffect } from "react";
import usePlacesWidget from "react-google-autocomplete/lib/usePlacesWidget";
import locationService, {
  Country,
  State,
  City,
} from "../../services/locationService";
import { Field } from "../ui/Field";
import { Input, InputGroup, InputAddon } from "../ui/Input";
import { Callout } from "../ui/Callout";
import { Badge } from "../ui/Badge";
import { Icon } from "../ui/Icon";

interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GooglePlaceResult {
  formatted_address: string;
  address_components: GoogleAddressComponent[];
  geometry: {
    location: {
      lat: () => number;
      lng: () => number;
    };
  };
  place_id: string;
}

export interface LocationData {
  country_id?: number | null;
  state_id?: number | null;
  city_id?: number | null;
  city_other?: string;
  street?: string;
  formatted_address?: string;
  place_id?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

interface GooglePlacesAutocompleteProps {
  onLocationSelect: (locationData: LocationData) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  value?: string;
  isRTL?: boolean;
  countryRestriction?: string;
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  onLocationSelect,
  placeholder = "Search for an address...",
  label = "Address Search",
  error,
  helperText,
  disabled = false,
  value = "",
  countryRestriction,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<string>("");
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState(value);

  const apiKey =
    process.env.REACT_APP_GOOGLE_MAP_PLACE_KEY ||
    process.env.REACT_GOOGLE_MAP_PLACE_KEY;

  const extractAddressComponents = (
    addressComponents: GoogleAddressComponent[]
  ) => {
    const components = {
      country: "",
      countryCode: "",
      state: "",
      stateCode: "",
      city: "",
      sublocality: "",
      route: "",
      street_number: "",
      postal_code: "",
    };

    addressComponents.forEach((component) => {
      const types = component.types;
      if (types.includes("country")) {
        components.country = component.long_name;
        components.countryCode = component.short_name;
      }
      if (types.includes("administrative_area_level_1")) {
        components.state = component.long_name;
        components.stateCode = component.short_name;
      }
      if (types.includes("locality") || types.includes("administrative_area_level_2")) {
        components.city = component.long_name;
      }
      if (types.includes("sublocality") || types.includes("sublocality_level_1")) {
        components.sublocality = component.long_name;
      }
      if (types.includes("route")) {
        components.route = component.long_name;
      }
      if (types.includes("street_number")) {
        components.street_number = component.long_name;
      }
      if (types.includes("postal_code")) {
        components.postal_code = component.long_name;
      }
    });

    return components;
  };

  const convertAddressToIds = async (
    components: ReturnType<typeof extractAddressComponents>
  ) => {
    try {
      let countryId: number | null = null;
      let stateId: number | null = null;
      let cityId: number | null = null;
      let cityOther = "";

      if (components.country || components.countryCode) {
        const countriesResponse = await locationService.getCountries({
          search: components.country || components.countryCode,
          per_page: 5,
        });

        const countries =
          countriesResponse?.data?.data || countriesResponse?.data || [];
        const matchingCountry = countries.find(
          (country: Country) =>
            country.name.toLowerCase() === components.country.toLowerCase() ||
            country.iso2.toLowerCase() === components.countryCode.toLowerCase() ||
            country.iso3.toLowerCase() === components.countryCode.toLowerCase()
        );

        if (matchingCountry) countryId = matchingCountry.id;
      }

      if (countryId && (components.state || components.stateCode)) {
        const statesResponse = await locationService.getStates({
          country_id: countryId,
          search: components.state || components.stateCode,
          per_page: 10,
        });

        const states = statesResponse?.data?.data || [];
        const matchingState = states.find(
          (state: State) =>
            state.name.toLowerCase() === components.state.toLowerCase() ||
            state.state_code.toLowerCase() === components.stateCode.toLowerCase()
        );

        if (matchingState) stateId = matchingState.id;
      }

      if (stateId && (components.city || components.sublocality)) {
        const cityName = components.city || components.sublocality;
        const citiesResponse = await locationService.getCities({
          state_id: stateId,
          search: cityName,
          per_page: 10,
        });

        const cities = citiesResponse?.data?.data || [];
        const matchingCity = cities.find(
          (city: City) => city.name.toLowerCase() === cityName.toLowerCase()
        );

        if (matchingCity) {
          cityId = matchingCity.id;
        } else {
          cityOther = cityName;
        }
      } else if (components.city || components.sublocality) {
        cityOther = components.city || components.sublocality;
      }

      return { country_id: countryId, state_id: stateId, city_id: cityId, city_other: cityOther };
    } catch (err) {
      console.error("Error converting address to IDs:", err);
      throw new Error("Failed to convert address to database IDs");
    }
  };

  const handlePlaceSelection = async (place: GooglePlaceResult) => {
    if (!place || !place.address_components) return;

    setIsLoading(true);
    setConversionError(null);
    setSelectedPlace(place.formatted_address);

    try {
      const components = extractAddressComponents(place.address_components);
      const locationIds = await convertAddressToIds(components);

      const streetParts: string[] = [];
      if (components.street_number) streetParts.push(components.street_number);
      if (components.route) streetParts.push(components.route);
      const street = streetParts.join(" ") || "";

      const locationData: LocationData = {
        ...locationIds,
        street,
        formatted_address: place.formatted_address,
        place_id: place.place_id,
        coordinates: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
      };

      onLocationSelect(locationData);
    } catch (err) {
      console.error("Error processing selected place:", err);
      setConversionError(
        "Failed to process the selected address. Please try again or enter manually."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const { ref } = usePlacesWidget({
    apiKey: apiKey,
    onPlaceSelected: handlePlaceSelection,
    options: {
      types: ["address"],
      fields: ["formatted_address", "address_components", "geometry", "place_id"],
      ...(countryRestriction
        ? { componentRestrictions: { country: countryRestriction.toLowerCase() } }
        : {}),
    },
  });

  const handleClear = () => {
    setInputValue("");
    setSelectedPlace("");
    setConversionError(null);
    onLocationSelect({
      country_id: null,
      state_id: null,
      city_id: null,
      city_other: "",
      street: "",
      formatted_address: "",
      place_id: "",
    });
    if (ref.current) ref.current.value = "";
  };

  // Theme-aware styles for the Google Places dropdown (.pac-container).
  // Uses CSS variables so it tracks light/dark mode automatically.
  useEffect(() => {
    const id = "almaship-pac-styles";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      .pac-container {
        z-index: 9999 !important;
        border-radius: var(--r-md) !important;
        box-shadow: var(--shd-3) !important;
        border: 1px solid var(--line) !important;
        background: var(--bg-elev) !important;
        margin-top: 4px !important;
        font-family: var(--font-sans) !important;
        padding: 4px !important;
      }
      .pac-item {
        padding: 10px 12px !important;
        border: none !important;
        border-radius: var(--r-sm) !important;
        cursor: pointer !important;
        font-family: var(--font-sans) !important;
        color: var(--ink) !important;
      }
      .pac-item:hover,
      .pac-item-selected {
        background-color: var(--bg-tint) !important;
      }
      .pac-matched {
        font-weight: 600 !important;
        color: var(--brand-blue) !important;
      }
      .pac-item-query {
        font-size: 14px !important;
        color: var(--ink) !important;
      }
      .pac-secondary-text {
        font-size: 12px !important;
        color: var(--ink-3) !important;
      }
      .pac-icon { margin-inline-end: 10px !important; }
    `;
    document.head.appendChild(style);
  }, []);

  if (!apiKey) {
    return (
      <Callout tone="warn" style={{ marginBottom: 8 }}>
        <Icon name="alert" size={16} />
        <span>Google Places API key is not configured. Please set the key in your environment variables.</span>
      </Callout>
    );
  }

  const showClear = (inputValue || selectedPlace) && !disabled;

  return (
    <div style={{ position: "relative", zIndex: 1 }}>
      <Field label={label} hint={error || helperText} htmlFor="google-places-input">
        <InputGroup>
          <InputAddon>
            {isLoading ? (
              <Icon name="refresh" size={14} color="var(--ink-3)" />
            ) : (
              <Icon name="mapPin" size={14} color="var(--ink-3)" />
            )}
          </InputAddon>
          <Input
            id="google-places-input"
            ref={ref as React.Ref<HTMLInputElement>}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            style={error ? { borderColor: "var(--err)" } : undefined}
          />
          {showClear && (
            <InputAddon>
              <button
                type="button"
                onClick={handleClear}
                aria-label="Clear address"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--ink-3)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 2,
                }}
              >
                <Icon name="x" size={13} />
              </button>
            </InputAddon>
          )}
        </InputGroup>
      </Field>

      {conversionError && (
        <Callout tone="warn" style={{ marginTop: 8 }}>
          <Icon name="alert" size={16} />
          <span>{conversionError}</span>
        </Callout>
      )}

      {selectedPlace && !isLoading && (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <Badge tone="info" dot>
            <Icon name="sparkle" size={12} />
            <span style={{ marginInlineStart: 4 }}>
              Selected: {selectedPlace.length > 60 ? `${selectedPlace.slice(0, 60)}…` : selectedPlace}
            </span>
          </Badge>
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={handleClear}
            aria-label="Clear selection"
          >
            <Icon name="x" size={11} />
          </button>
        </div>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
